/**
 * TrainingSessionAudioControls
 *
 * Provides voice selection and autoplay for completed training sessions.
 * Uses the scenario's voiceConfig for default patient voice.
 * Adapted from SimulationAudioControls.js
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, FormControl, InputLabel, Select, MenuItem,
  Typography, Alert, CircularProgress
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ConversationAutoplay from '../therapist/ConversationAutoplay';
import TrainingSessionAudioExportDialog from './TrainingSessionAudioExportDialog';
import { getElevenLabsVoices } from '../../services/api';

const TrainingSessionAudioControls = ({ transcript, sessionId, voiceConfig }) => {
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [voiceError, setVoiceError] = useState(null);
  const [studentVoiceId, setStudentVoiceId] = useState('');
  const [patientVoiceId, setPatientVoiceId] = useState('');
  const [showAutoplay, setShowAutoplay] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        setLoadingVoices(true);
        setVoiceError(null);
        const voiceList = await getElevenLabsVoices();
        setVoices(voiceList);

        if (voiceList.length > 0) {
          // Student voice: default to Rachel/Sarah
          const defaultStudent = voiceList.find(v =>
            v.name?.toLowerCase().includes('rachel') ||
            v.name?.toLowerCase().includes('sarah')
          ) || voiceList[0];
          setStudentVoiceId(defaultStudent.voice_id || '');

          // Patient voice: use scenario voiceConfig if set, otherwise Adam/Josh
          if (voiceConfig?.patientVoiceId) {
            const scenarioVoice = voiceList.find(v => v.voice_id === voiceConfig.patientVoiceId);
            setPatientVoiceId(scenarioVoice ? scenarioVoice.voice_id : voiceConfig.patientVoiceId);
          } else {
            const defaultPatient = voiceList.find(v =>
              v.name?.toLowerCase().includes('adam') ||
              v.name?.toLowerCase().includes('josh')
            ) || voiceList[Math.min(1, voiceList.length - 1)];
            setPatientVoiceId(defaultPatient.voice_id || '');
          }
        }
      } catch (err) {
        console.error('Failed to fetch ElevenLabs voices:', err);
        setVoiceError('Failed to load available voices. Check ElevenLabs API key configuration.');
      } finally {
        setLoadingVoices(false);
      }
    };
    fetchVoices();
  }, [voiceConfig]);

  // Convert training transcript to ConversationAutoplay message format
  const convertedMessages = useMemo(() => {
    if (!transcript) return [];
    return transcript.map((turn, idx) => {
      let senderRole;
      switch (turn.actor) {
        case 'patient': senderRole = 'client'; break;
        case 'student': senderRole = 'ai'; break;
        case 'system': senderRole = 'system'; break;
        default: senderRole = 'ai';
      }
      return {
        _id: `train-turn-${idx}`,
        text: turn.content,
        senderRole,
        timestamp: turn.timestamp || new Date(),
      };
    });
  }, [transcript]);

  // Synthetic platformSettings so ConversationAutoplay uses admin-chosen voices
  const syntheticPlatformSettings = useMemo(() => ({
    voiceSettings: {
      defaultAITherapistVoiceId: studentVoiceId,
      defaultClientVoiceId: patientVoiceId,
      defaultHumanTherapistVoiceId: studentVoiceId,
      defaultSystemVoiceId: studentVoiceId,
    }
  }), [studentVoiceId, patientVoiceId]);

  const canPlay = studentVoiceId && patientVoiceId && convertedMessages.length > 0;

  if (loadingVoices) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading voices...</Typography>
      </Box>
    );
  }

  if (voiceError) {
    return <Alert severity="warning" sx={{ mb: 1 }}>{voiceError}</Alert>;
  }

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        p: 1.5, bgcolor: 'grey.50', borderRadius: 1, flexWrap: 'wrap'
      }}>
        <VolumeUpIcon color="action" fontSize="small" />
        <Typography variant="subtitle2" sx={{ mr: 0.5 }}>Audio:</Typography>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Student Voice</InputLabel>
          <Select value={studentVoiceId} onChange={(e) => setStudentVoiceId(e.target.value)} label="Student Voice">
            {voices.map(v => (
              <MenuItem key={v.voice_id} value={v.voice_id}>
                {v.name || v.voice_id}{v.category ? ` (${v.category})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Patient Voice</InputLabel>
          <Select value={patientVoiceId} onChange={(e) => setPatientVoiceId(e.target.value)} label="Patient Voice">
            {voices.map(v => (
              <MenuItem key={v.voice_id} value={v.voice_id}>
                {v.name || v.voice_id}{v.category ? ` (${v.category})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained" size="small"
          startIcon={<PlayArrowIcon />}
          onClick={() => setShowAutoplay(true)}
          disabled={!canPlay}
        >
          Play Conversation
        </Button>

        <Button
          variant="outlined" size="small"
          startIcon={<DownloadIcon />}
          onClick={() => setShowExportDialog(true)}
          disabled={!canPlay}
        >
          Export Audio
        </Button>
      </Box>

      {showAutoplay && (
        <ConversationAutoplay
          messages={convertedMessages}
          userSettings={null}
          platformSettings={syntheticPlatformSettings}
          therapistSettings={null}
          clientId={null}
          conversationId={null}
          hideExportOption={true}
          token={localStorage.getItem('token')}
          onClose={() => setShowAutoplay(false)}
        />
      )}

      {showExportDialog && (
        <TrainingSessionAudioExportDialog
          open={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          sessionId={sessionId}
          messageCount={convertedMessages.length}
          studentVoiceId={studentVoiceId}
          patientVoiceId={patientVoiceId}
        />
      )}
    </>
  );
};

export default TrainingSessionAudioControls;
